import React, { useState } from 'react';
import { Form, Button, Card, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { db, auth } from '../../services/firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp, 
  collection,
  Timestamp,
  runTransaction
} from 'firebase/firestore';

/**
 * Componente per attivare un abbonamento tramite codice di attivazione
 * @param {Object} props - Props del componente
 * @param {Function} props.onSuccess - Callback chiamata dopo l'attivazione con successo
 */
const ActivationCodeForm = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Formatta il codice mentre l'utente digita (XXXX-XXXX-XXXX)
  const handleCodeChange = (e) => {
    let inputCode = e.target.value.toUpperCase();
    
    // Rimuovi tutti i caratteri non alfanumerici
    inputCode = inputCode.replace(/[^A-Z0-9]/g, '');
    
    // Aggiungi trattini ogni 4 caratteri
    if (inputCode.length > 0) {
      // Dividi in gruppi di 4 caratteri e unisci con trattini
      let formattedCode = '';
      for (let i = 0; i < inputCode.length && i < 12; i += 4) {
        const chunk = inputCode.substring(i, Math.min(i + 4, inputCode.length));
        formattedCode += (i > 0 ? '-' : '') + chunk;
      }
      inputCode = formattedCode;
    }
    
    setCode(inputCode);
  };

  const activateSubscription = async () => {
    if (!auth.currentUser) {
      setError(t('common.error') + ': ' + t('auth.login.errors.generic'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Rimuovi i trattini dal codice
      const cleanCode = code.replace(/-/g, '');
      
      // Verifica il formato del codice
      if (cleanCode.length !== 12) {
        setError(t('settings.subscription.activation.codeIncomplete'));
        setLoading(false);
        return;
      }

      // Usa l'ID del documento come codice (senza trattini)
      const codeDocId = `code_${cleanCode.toLowerCase()}`;
      const codeRef = doc(db, 'activationCodes', codeDocId);
      
      try {
        // Usa una transazione per verificare e aggiornare il codice atomicamente
        await runTransaction(db, async (transaction) => {
          // PRIMA ESEGUIAMO TUTTE LE LETTURE
          // 1. Leggi il documento del codice
          const codeDocSnapshot = await transaction.get(codeRef);
          
          // Verifica se il codice esiste
          if (!codeDocSnapshot.exists()) {
            throw new Error(t('settings.subscription.activation.invalidCode'));
          }
          
          const codeDataSnapshot = codeDocSnapshot.data();
          
          // Verifica se il codice è già stato utilizzato
          if (codeDataSnapshot.isUsed) {
            throw new Error(t('settings.subscription.activation.invalidCode'));
          }
          
          // 2. Leggi il documento dell'abbonamento dell'utente
          const currentUserId = auth.currentUser.uid;
          const userSubscriptionRef = doc(db, `users/${currentUserId}/subscription/default`);
          const userSubscriptionSnapshot = await transaction.get(userSubscriptionRef);
          const existingUserSubscription = userSubscriptionSnapshot.exists() ? userSubscriptionSnapshot.data() : null;
          
          // Calcola la data di fine basata sulla durata del codice
          const currentDate = new Date();
          const endDateTimestamp = new Date(currentDate.getTime() + (codeDataSnapshot.duration * 24 * 60 * 60 * 1000));
          
          // ORA ESEGUIAMO TUTTE LE SCRITTURE
          // 1. Aggiorna il documento del codice di attivazione
          transaction.update(codeRef, {
            isUsed: true,
            usedBy: currentUserId,
            usedAt: serverTimestamp()
          });
          
          // 2. Crea il nuovo abbonamento
          const updatedSubscription = {
            plan: 'premium',
            status: 'active',
            isActive: true,
            deviceLimit: Infinity,
            partLimit: Infinity,
            startDate: serverTimestamp(),
            endDate: Timestamp.fromDate(endDateTimestamp),
            updatedAt: serverTimestamp(),
            activatedByCode: true,
            activationCode: code,
            // Mantieni i contatori esistenti se disponibili
            devicesCreated: existingUserSubscription?.devicesCreated || 0,
            partsCreated: existingUserSubscription?.partsCreated || 0
          };
          
          // 3. Aggiorna l'abbonamento
          if (userSubscriptionSnapshot.exists()) {
            transaction.update(userSubscriptionRef, updatedSubscription);
          } else {
            transaction.set(userSubscriptionRef, updatedSubscription);
          }
          
          return { success: true };
        });
        
        // Mostra il messaggio di successo
        setSuccess(t('settings.subscription.activation.success'));
        
        // Chiama la funzione di callback dopo un breve ritardo
        setTimeout(() => {
          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess({
              plan: 'premium',
              status: 'active',
              isActive: true
            });
          }
        }, 1500);
        
      } catch (transactionError) {
        // Gestisce errori specifici della transazione
        console.error('Errore nella transazione:', transactionError);
        setError(transactionError.message || t('settings.subscription.activation.error'));
      }
      
    } catch (error) {
      console.error('Errore durante l\'attivazione del codice:', error);
      setError(t('settings.subscription.activation.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header as="h5" className="bg-primary text-white">
        <FontAwesomeIcon icon={faKey} className="me-2" />
        {t('settings.subscription.activation.title')}
      </Card.Header>
      <Card.Body>
        <p className="text-muted">
          {t('settings.subscription.activation.description')}
        </p>
        
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            {success}
          </Alert>
        )}
        
        <Form onSubmit={(e) => { e.preventDefault(); activateSubscription(); }}>
          <Form.Group className="mb-3">
            <Form.Label>{t('settings.subscription.activation.codeLabel')}</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={code}
                onChange={handleCodeChange}
                placeholder="XXXX-XXXX-XXXX"
                disabled={loading}
                className="text-uppercase"
                maxLength={14}
              />
            </InputGroup>
            <Form.Text muted>
              {t('settings.subscription.activation.codeHelp')}
            </Form.Text>
          </Form.Group>
          
          <div className="d-grid">
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading || code.replace(/-/g, '').length !== 12}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
                  {t('common.loading')}
                </>
              ) : (
                t('settings.subscription.activation.activate')
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ActivationCodeForm; 